import { useState } from 'react';
import { clsx } from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useToast } from '@/shared/ui/Toast';
import { ConfirmDialog } from '@/shared/ui/Modal';
import {
  reviewSmsApi,
  type FollowUp,
  type InboxFilter,
  type InboxRow,
  type ThreadMessage,
} from '../api/reviewSmsApi';
import { reviewKeys } from '../queryKeys';
import { errorMessage, useCanDeleteConversations, useCanEditProgram } from './hooks';
import { formatLocalTime, formatMessageTime, relativeTime } from './format';
import { TypeDeleteDialog } from './TypeDeleteDialog';
import styles from './TextRequests.module.css';

const FILTERS: { value: InboxFilter; label: string }[] = [
  { value: 'needs_you', label: 'Needs you' },
  { value: 'posted', label: 'Posted' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'all', label: 'All' },
];

const STATE_LABEL: Record<string, string> = {
  scheduled: 'Waiting to send',
  invited: 'Waiting for reply',
  rated: 'Rated',
  drafting: 'Answering questions',
  draft_sent: 'Draft sent',
  handoff: 'Link sent',
  posted: 'Posted on Google',
  private_ack: 'Private feedback',
  private_feedback: 'Private feedback',
  private_ask: 'Asked what could be better',
  rating_asked: 'Waiting for rating',
  offer_help: 'Asked to help with a review',
  ask_theme: 'Answering questions',
  ask_detail: 'Answering questions',
  ask_reason: 'Answering questions',
  ask_people: 'Answering questions',
  draft_review: 'Reviewing the draft',
  own_words: 'Writing their own words',
  staff_needed: 'Needs the office',
  clinical: 'Asked a question',
  unclear: 'Unclear reply',
  wrong_number: 'Wrong number',
  complaint: 'Complaint',
  opted_out: 'Opted out',
  closed: 'Closed',
};

function stateLabel(c: InboxRow): string {
  if (c.closedReason === 'review_matched') return 'Posted on Google';
  if (c.closedReason === 'opted_out') return 'Opted out';
  if (c.closedReason === 'declined') return 'Declined';
  if (c.closedReason === 'stale') return 'Not sent (too late)';
  if (c.closedReason === 'open_conversation') return 'Not sent · already in a conversation';
  if (c.closedReason === 'frequency_cap') return 'Not sent · texted in the last 60 days';
  if (c.closedReason === 'superseded_test') return 'Replaced by a newer conversation';
  if (c.closedReason === 'closed_by_staff') return 'Closed by staff';
  if (c.closedReason === 'program_not_live') return "Not sent · texting wasn't live";
  if (c.closedReason === 'test_mode') return 'Not sent · test mode was on';
  if (c.closedReason === 'send_failed') return 'Not sent · the text could not go out';
  if (c.closedAt && c.closedReason) return `Closed · ${c.closedReason.replace(/_/g, ' ')}`;
  return STATE_LABEL[c.state] ?? c.state.replace(/_/g, ' ');
}

/** The pill: why staff are needed, else where the thread is (and that the office called back). */
function pillText(c: InboxRow): string {
  if (c.needsYouReason) return c.needsYouReason;
  return c.followUp?.calledBackAt ? `${stateLabel(c)} · called back` : stateLabel(c);
}

function pillClass(c: InboxRow): string {
  if (c.bucket === 'needs_you')
    return c.needsYouReason === 'Taken over' ? styles.pillInfo : styles.pillError;
  if (c.bucket === 'posted') return styles.pillLive;
  return styles.pillMuted;
}

function Bubble({ m, timezone }: { m: ThreadMessage; timezone: string }) {
  if (
    m.author === 'system' ||
    (m.direction === 'outbound' && m.kind === 'confirmation' && !m.body)
  ) {
    return <div className={styles.systemNote}>{m.body ?? m.kind}</div>;
  }
  const inbound = m.direction === 'inbound';
  const staff = m.author === 'staff';
  const who = inbound ? 'Patient' : staff ? 'Staff' : 'Betty · auto';
  const status =
    m.status === 'failed'
      ? ` · failed${m.errorCode ? ` (${m.errorCode})` : ''}`
      : m.status === 'delivered'
        ? ' · delivered'
        : '';
  return (
    <div
      className={clsx(
        styles.bubble,
        inbound ? styles.bubbleIn : staff ? styles.bubbleStaff : styles.bubbleOut,
      )}
    >
      {m.body ?? <em>(body no longer kept)</em>}
      <div className={styles.bubbleMeta}>
        {who} · {formatMessageTime(m.createdAt, timezone)}
        {status}
      </div>
    </div>
  );
}

/** What the office was asked to do, by alert kind. */
const FOLLOW_UP_ASK: Record<FollowUp['kind'], string> = {
  private_feedback:
    'The patient was told the front desk will reach out. Call them or reply here, then mark it called back.',
  clinical: 'Betty stopped and alerted the office. Call the patient or reply here, then mark it.',
  unclear: 'Betty stopped and alerted the office. Call the patient or reply here, then mark it.',
  complaint: 'Betty stopped and alerted the office. Call the patient or reply here, then mark it.',
  wrong_number:
    'This number isn’t the patient’s. Fix the number in your records, then mark it handled.',
};

function FollowUpPanel({
  locationId,
  c,
  timezone,
  onChanged,
}: {
  locationId: string;
  c: InboxRow & { followUp: FollowUp };
  timezone: string;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const f = c.followUp;
  const handled = f.kind === 'wrong_number' ? 'handled' : 'called back';
  const save = useMutation({
    mutationFn: (done: boolean) =>
      reviewSmsApi.setFollowUp(
        locationId,
        c.id,
        done ? { done, note: note.trim() || null } : { done },
      ),
    onSuccess: (_r, done) => {
      setNote('');
      onChanged();
      toast.success(done ? `Marked ${handled}.` : 'Undone. It’s back in Needs you.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  if (f.waiting)
    return (
      <div className={styles.followUp}>
        <div className={styles.followUpText}>
          {c.needsYouReason && <strong>{c.needsYouReason}: </strong>}
          {FOLLOW_UP_ASK[f.kind]}
        </div>
        <div className={styles.followUpRow}>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={280}
            placeholder="Note (optional), e.g. Left a voicemail"
            aria-label="Follow-up note"
          />
          <Button size="sm" isLoading={save.isPending} onClick={() => save.mutate(true)}>
            Mark {handled}
          </Button>
        </div>
      </div>
    );
  // Out of Needs you after 30 days, but a late call still counts once it is marked.
  if (f.olderOpen && !f.calledBackAt)
    return (
      <div className={styles.followUpDone}>
        <span>Over 30 days old and no longer in Needs you. Called them since?</span>
        <button
          type="button"
          className={styles.textButton}
          disabled={save.isPending}
          onClick={() => save.mutate(true)}
        >
          Mark {handled}
        </button>
      </div>
    );
  if (!f.calledBackAt) return null;
  return (
    <div className={styles.followUpDone}>
      <span>
        {f.kind === 'wrong_number' ? 'Handled' : 'Called back'} by {f.calledBackBy ?? 'staff'} ·{' '}
        {formatMessageTime(f.calledBackAt, timezone)}
        {f.note ? ` · “${f.note}”` : ''}
      </span>
      <button
        type="button"
        className={styles.textButton}
        disabled={save.isPending}
        onClick={() => save.mutate(false)}
      >
        Undo
      </button>
    </div>
  );
}

function Thread({
  locationId,
  id,
  timezone,
  onDeleted,
}: {
  locationId: string;
  id: string;
  timezone: string;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const canDelete = useCanDeleteConversations();
  const canClose = useCanEditProgram();
  const [reply, setReply] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const thread = useQuery({
    queryKey: reviewKeys.smsThread(locationId, id),
    queryFn: () => reviewSmsApi.getThread(locationId, id),
    refetchInterval: 15_000,
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: reviewKeys.smsThread(locationId, id) });
    queryClient.invalidateQueries({ queryKey: reviewKeys.smsInboxes(locationId) });
  };
  const takeOver = useMutation({
    mutationFn: () => reviewSmsApi.takeOver(locationId, id),
    onSuccess: () => {
      invalidate();
      toast.success('You have this thread. Betty is paused until you hand it back.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const handBack = useMutation({
    mutationFn: () => reviewSmsApi.handBack(locationId, id),
    onSuccess: () => {
      invalidate();
      toast.success('Handed back to Betty.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const close = useMutation({
    mutationFn: () => reviewSmsApi.closeConversation(locationId, id),
    onSuccess: () => {
      setConfirmClose(false);
      invalidate();
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsDays(locationId) });
      toast.success('Conversation closed. Betty won’t text in it again; you can start a new one.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => reviewSmsApi.deleteConversation(locationId, id),
    onSuccess: (r) => {
      setConfirmDelete(false);
      queryClient.removeQueries({ queryKey: reviewKeys.smsThread(locationId, id) });
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsInboxes(locationId) });
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsDays(locationId) });
      // The Get reviews "need you" count and Performance count the deleted thread too.
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsSummaries(locationId) });
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsPerformances(locationId) });
      toast.success(
        r.mode === 'deleted'
          ? 'Test conversation deleted.'
          : 'Conversation deleted. Its texts are erased; the patient still won’t be asked again for 60 days.',
      );
      onDeleted();
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const send = useMutation({
    mutationFn: () => reviewSmsApi.reply(locationId, id, reply),
    onSuccess: (r) => {
      invalidate();
      if (r.sent) {
        setReply('');
        toast.success('Sent from your practice number.');
      } else {
        toast.warning(
          r.reason === 'quiet_hours'
            ? `Outside the patient’s texting hours; try again ${r.retryAt ? `after ${formatLocalTime(r.retryAt, timezone)}` : 'later'}.`
            : `Not sent: ${r.reason.replace(/_/g, ' ')}.`,
        );
      }
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  if (thread.isLoading) return <div className={styles.thread}>Loading…</div>;
  if (!thread.data) return <div className={styles.thread}>This thread could not be loaded.</div>;
  const c = thread.data.conversation;
  const open = !c.closedAt;
  const name = c.visit?.patient ?? c.participant.displayName ?? 'Patient';

  return (
    <div className={styles.thread}>
      <div className={styles.threadHeader}>
        <div>
          <div className={styles.threadTitle}>
            {name}
            {c.visit?.isTest && <span className={styles.pillInfo}> Test</span>}
          </div>
          <div className={styles.hint}>
            {c.visit?.providerName ? `${c.visit.providerName} · ` : ''}
            {c.visit ? visitLine(c.visit, timezone) : ''} · {c.visit?.mobileMasked ?? ''}
          </div>
        </div>
        <div className={styles.headerRight}>
          <span className={pillClass(c)}>{pillText(c)}</span>
          {open && !c.automationPaused && (
            <Button
              variant="secondary"
              size="sm"
              isLoading={takeOver.isPending}
              onClick={() => takeOver.mutate()}
            >
              Take over
            </Button>
          )}
          {open && c.automationPaused && (
            <Button
              variant="secondary"
              size="sm"
              isLoading={handBack.isPending}
              onClick={() => handBack.mutate()}
            >
              Hand back to Betty
            </Button>
          )}
          {open && canClose && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmClose(true)}>
              Close
            </Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={() => close.mutate()}
        title={c.visit?.isTest ? 'Close this test conversation?' : 'Close this conversation?'}
        message={
          c.visit?.isTest
            ? 'Betty stops here and the thread stays in your inbox, closed. You can send a new test to the same phone.'
            : 'Betty stops texting this patient and the thread stays in your inbox, closed. If they reply, you will still see it here. They won’t get another review request for 60 days.'
        }
        confirmLabel="Close conversation"
        isLoading={close.isPending}
      />
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        title={c.visit?.isTest ? 'Delete this test conversation?' : 'Delete this conversation?'}
        message={
          c.visit?.isTest
            ? 'This removes the test thread, its test visit on Today and its draft. Your numbers do not change, because tests never count.'
            : 'This erases every text in this thread and removes it from your inbox. It cannot be undone. Betty stops texting this patient, and they still won’t get another review request for 60 days. Any review they already posted on Google is not affected.'
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={remove.isPending}
      />
      <div className={styles.messages}>
        {thread.data.messages.map((m) => (
          <Bubble key={m.id} m={m} timezone={timezone} />
        ))}
        {!c.followUp &&
          c.needsYouReason &&
          c.needsYouReason !== 'Taken over' &&
          c.needsYouReason !== 'New reply' && (
            <div className={styles.systemNote}>
              {c.needsYouReason}: Betty stopped and alerted the office. Reply here or call.
            </div>
          )}
      </div>
      {c.followUp && (
        <FollowUpPanel
          locationId={locationId}
          c={{ ...c, followUp: c.followUp }}
          timezone={timezone}
          onChanged={() => {
            invalidate();
            // The Get reviews "need you" count and Performance's "Followed up" read the same marks.
            queryClient.invalidateQueries({ queryKey: reviewKeys.smsSummaries(locationId) });
            queryClient.invalidateQueries({ queryKey: reviewKeys.smsPerformances(locationId) });
          }}
        />
      )}
      {open && (
        <div className={styles.replyRow}>
          <Input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={
              c.automationPaused
                ? 'Reply from your practice number…'
                : 'Take over to reply, or type to take over and send'
            }
            aria-label="Reply"
            onKeyDown={(e) => {
              // One send per Enter: not while one is in flight (a double SMS), nor while an IME
              // is still composing a character.
              if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
              if (reply.trim() && !send.isPending) send.mutate();
            }}
          />
          <Button
            size="sm"
            isLoading={send.isPending}
            disabled={!reply.trim()}
            onClick={() => send.mutate()}
          >
            Send
          </Button>
        </div>
      )}
    </div>
  );
}

export function ConversationsTab({
  locationId,
  timezone,
  selectedId,
  onSelect,
}: {
  locationId: string;
  /** The practice's zone (Location.timezone): every time here is shown in it, never the viewer's. */
  timezone: string;
  /** The open thread, from the URL (?conversation=), e.g. when linked from the Today table. */
  selectedId: string | null;
  onSelect: (conversationId: string | null) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const canDelete = useCanDeleteConversations();
  const [filter, setFilter] = useState<InboxFilter>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const inbox = useQuery({
    queryKey: reviewKeys.smsInbox(locationId, filter),
    queryFn: () => reviewSmsApi.listInbox(locationId, filter),
    enabled: !!locationId,
    refetchInterval: 30_000,
  });
  const rows = inbox.data?.rows ?? [];
  const counts = inbox.data?.counts;
  const active = selectedId ?? rows[0]?.id ?? null;
  // Only rows still on screen count, so a refetch that drops one never deletes it unseen.
  const selected = rows.filter((r) => picked.has(r.id));
  const selectedTests = selected.filter((r) => r.visit?.isTest).length;
  const selectedPatients = selected.length - selectedTests;
  const testIds = rows.filter((r) => r.visit?.isTest).map((r) => r.id);
  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const bulkDelete = useMutation({
    mutationFn: () =>
      reviewSmsApi.bulkDeleteConversations(
        locationId,
        selected.map((r) => r.id),
      ),
    onSuccess: (r) => {
      setConfirmBulk(false);
      setPicked(new Set());
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsInboxes(locationId) });
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsDays(locationId) });
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsSummaries(locationId) });
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsPerformances(locationId) });
      if (active && selected.some((c) => c.id === active)) onSelect(null);
      const done = r.deleted + r.redacted;
      if (r.failed.length > 0)
        toast.warning(`${done} deleted; ${r.failed.length} couldn’t be: ${r.failed[0].message}`);
      else toast.success(`${done} conversation${done === 1 ? '' : 's'} deleted.`);
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  return (
    <div className={styles.inbox}>
      <div className={styles.inboxList}>
        <div className={styles.inboxFilters} role="tablist" aria-label="Inbox filter">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={filter === f.value}
              className={clsx(styles.subTab, filter === f.value && styles.subTabActive)}
              onClick={() => {
                setFilter(f.value);
                setPicked(new Set());
                onSelect(null);
              }}
            >
              {f.label}
              {counts && counts[f.value] > 0 && f.value === 'needs_you' && (
                <span className={styles.countBadge}>{counts[f.value]}</span>
              )}
            </button>
          ))}
        </div>
        {canDelete && rows.length > 0 && (
          <div className={styles.bulkBar}>
            <label className={styles.bulkCheck}>
              <input
                type="checkbox"
                checked={selected.length > 0 && selected.length === rows.length}
                ref={(el) => {
                  if (el) el.indeterminate = selected.length > 0 && selected.length < rows.length;
                }}
                onChange={(e) =>
                  setPicked(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
                }
                aria-label="Select all conversations"
              />
              All
            </label>
            {testIds.length > 0 && (
              <button
                type="button"
                className={styles.textButton}
                onClick={() => setPicked(new Set(testIds))}
              >
                Select tests ({testIds.length})
              </button>
            )}
            {selected.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setConfirmBulk(true)}>
                Delete {selected.length}
              </Button>
            )}
          </div>
        )}
        {inbox.isLoading && <div className={styles.tableEmpty}>Loading…</div>}
        {!inbox.isLoading && rows.length === 0 && (
          <div className={styles.tableEmpty}>
            {filter === 'all'
              ? 'No conversations yet. They appear here once Betty texts a confirmed visit.'
              : 'Nothing here right now.'}
          </div>
        )}
        {rows.map((c) => (
          <div
            key={c.id}
            className={clsx(styles.inboxRow, active === c.id && styles.inboxItemActive)}
          >
            {canDelete && (
              <input
                type="checkbox"
                className={styles.rowCheck}
                checked={picked.has(c.id)}
                onChange={() => toggle(c.id)}
                aria-label={`Select ${c.visit?.patient ?? c.participant.displayName ?? 'conversation'}`}
              />
            )}
            <button type="button" className={styles.inboxItem} onClick={() => onSelect(c.id)}>
              <span className={styles.inboxItemTop}>
                <span className={styles.inboxName}>
                  {c.visit?.patient ?? c.participant.displayName ?? 'Patient'}
                  {c.visit?.isTest && <span className={styles.pillInfo}> Test</span>}
                  {c.visit?.providerName ? (
                    <span className={styles.hint}> · {c.visit.providerName}</span>
                  ) : null}
                </span>
                <span className={styles.inboxTime}>
                  {relativeTime(c.lastInboundAt ?? c.lastOutboundAt ?? c.updatedAt)}
                </span>
              </span>
              <span className={pillClass(c)}>{pillText(c)}</span>
            </button>
          </div>
        ))}
      </div>
      <TypeDeleteDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        onConfirm={() => bulkDelete.mutate()}
        isLoading={bulkDelete.isPending}
        title={`Delete ${selected.length} conversation${selected.length === 1 ? '' : 's'}?`}
      >
        <ul className={styles.bulkList}>
          {selectedTests > 0 && (
            <li>
              {selectedTests} test conversation{selectedTests === 1 ? '' : 's'}: removed completely,
              with {selectedTests === 1 ? 'its' : 'their'} test visit
              {selectedTests === 1 ? '' : 's'} on Today.
            </li>
          )}
          {selectedPatients > 0 && (
            <li>
              {selectedPatients} patient conversation{selectedPatients === 1 ? '' : 's'}: every text
              is erased and the thread leaves your inbox. The visit and your numbers stay, Betty
              stops texting {selectedPatients === 1 ? 'this patient' : 'these patients'}, and they
              still won’t be asked again for 60 days.
            </li>
          )}
        </ul>
        <p className={styles.hint}>This can’t be undone.</p>
      </TypeDeleteDialog>
      {active ? (
        <Thread
          key={active}
          locationId={locationId}
          id={active}
          timezone={timezone}
          onDeleted={() => onSelect(null)}
        />
      ) : (
        <div className={styles.thread}>
          <EmptyState
            title="Pick a conversation"
            description="Every text Betty sends and every reply shows here, with take over when you need it."
          />
        </div>
      )}
    </div>
  );
}

/** "Visit 9:45 AM PT", or for a test "Test sent by Abhishek at 10:15 AM PT" (so a test nobody expected can be traced). */
function visitLine(visit: NonNullable<InboxRow['visit']>, timezone: string): string {
  if (visit.isTest && visit.testSentAt) {
    const when = formatMessageTime(visit.testSentAt, timezone);
    return visit.testSentBy
      ? `Test sent by ${visit.testSentBy} at ${when}`
      : `Test sent at ${when}`;
  }
  return `Visit ${formatMessageTime(visit.startsAt, timezone)}`;
}
